import Image from "next/image";
import Link from "next/link";
import { MinusIcon, PlusIcon } from "@heroicons/react/24/outline";
import Price from "@/components/price";
import { NOT_IMAGE } from "@/lib/constants";
import { PencilIcon, TrashIcon } from "@/components/icons/assets";
import type { CartItem } from "@/lib/bagisto/types";

interface CartRowProps {
    item: CartItem;
    currencyCode: string;
    onRemove: (id: string) => void;
    onDecrement: (item: CartItem) => void;
    onIncrement: (item: CartItem) => void;
    isUpdateLoading: boolean;
    isRemoveLoading: boolean;
}

export default function CartRow({
    item,
    currencyCode,
    onRemove,
    onDecrement,
    onIncrement,
    isUpdateLoading,
    isRemoveLoading,
}: CartRowProps) {
    const productHref = `/product-details/${item.product.urlKey}?type=${item.product.type}`;

    return (
        <div className="flex flex-col md:flex-row md:items-center justify-between">

            {/* Product — image + name */}
            <Link href={productHref} className="flex items-center gap-[17px] w-[219px]">
                <div className="w-[50px] h-[50px] rounded-[6px] bg-background-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    <Image
                        src={
                            item.product.cacheBaseImage?.at(0)?.smallImageUrl ||
                            item.product.images?.at(0)?.url ||
                            NOT_IMAGE
                        }
                        alt={item.product.name}
                        width={50}
                        height={50}
                        className="object-contain w-full h-full"
                        onError={(e) => (e.currentTarget.src = NOT_IMAGE)}
                    />
                </div>
                <span className="font-roboto font-medium text-[18px] leading-[25px] tracking-[0%] text-neutral-300 hover:text-primary-500 transition-colors line-clamp-1">
                    {item.product.name}
                </span>
            </Link>

            {/* Price */}
            <div className="w-[219px]">
                <Price
                    amount={item.price}
                    currencyCode={currencyCode}
                    className="font-roboto font-medium text-[18px] leading-[25px] tracking-[0%] text-neutral-300"
                />
            </div>

            {/* Quantity Stepper */}
            <div className="w-[219px]">
                <div className="flex items-center gap-2 h-[56px] px-3 py-2 rounded-[12px] bg-background-50 w-fit">
                    <button
                        aria-label="Decrease quantity"
                        onClick={() => onDecrement(item)}
                        disabled={isUpdateLoading || item.quantity <= 1}
                        className="w-9 h-9 bg-neutral-500 rounded-[8px] flex items-center justify-center cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex-shrink-0"
                    >
                        <MinusIcon className="h-6 w-6 text-white" />
                    </button>

                    <div className="w-[109px] h-[39px] px-4 py-2 rounded-[6px] flex items-center justify-center">
                        <span className="font-oswald font-medium text-base leading-[100%] text-neutral-500">
                            {String(item.quantity).padStart(2, "0")}
                        </span>
                    </div>

                    <button
                        aria-label="Increase quantity"
                        onClick={() => onIncrement(item)}
                        disabled={isUpdateLoading}
                        className="w-9 h-9 bg-neutral-500 rounded-[8px] flex items-center justify-center cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed transition-opacity flex-shrink-0"
                    >
                        <PlusIcon className="h-6 w-6 text-white" />
                    </button>
                </div>
            </div>

            {/* Subtotal */}
            <div className="w-[219px]">
                <Price
                    amount={item.total}
                    currencyCode={currencyCode}
                    className="font-roboto font-medium text-[18px] leading-[25px] tracking-[0%] text-neutral-300"
                />
            </div>

            {/* Edit + Delete */}
            <div className="flex items-center gap-3">
                {/* Edit — static */}
                <button
                    aria-label="Edit item"
                    className="w-9 h-9 flex items-center justify-center rounded-[12px] border-[1.38px] border-stroke-light p-[8.31px] text-neutral-30 cursor-pointer"
                >
                    <PencilIcon className="" />
                </button>

                {/* Delete */}
                <button
                    aria-label="Remove item"
                    onClick={() => onRemove(item.id)}
                    disabled={isRemoveLoading}
                    className="w-9 h-9 flex items-center justify-center rounded-[12px] border-[1.38px] border-stroke-light p-[8.31px] text-neutral-300 cursor-pointer disabled:opacity-50"
                >
                    <TrashIcon className="" />
                </button>
            </div>

        </div>
    );
}